import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Plus, Check, ArrowUpRight, X } from "lucide-react";
import PageHero from "../components/layout/PageHero";
import Reveal from "../components/Reveal";
import { useContent } from "../context/ContentContext";
import { useRFQ } from "../context/RFQContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";


export default function Products() {
  const { addToEnquiry, isInEnquiry, enquiry, removeFromEnquiry, openRFQ } = useRFQ();
  const { products, content } = useContent();
  // Filters follow the admin-managed category list (plus dosage forms).
  const filters = ["All Categories", ...(content.productCategories || []).map((c) => c.name), "Dosage Forms"];
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("All Categories");

  const filtered = useMemo(() => {
    return products.filter((p) => {
      const matchesFilter =
        filter === "All Categories" ||
        (filter === "Dosage Forms" ? p.group === "Dosage Forms" : p.category === filter);
      const matchesQuery = p.name.toLowerCase().includes(query.trim().toLowerCase());
      return matchesFilter && matchesQuery;
    });
  }, [query, filter, products]);

  return (
    <>
      <PageHero
        eyebrow="Products"
        title="Browse our pharmaceutical product directory"
        subtitle="Search and filter Novocreste products by category, then add items to your enquiry list — presented in a clean, export-style directory."
      />

      <section className="py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          {/* Controls */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
            <div className="relative w-full lg:max-w-md">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search products..."
                className="pl-10 h-12 rounded-full bg-card"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {filters.map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 h-10 rounded-full text-sm font-medium border transition-colors ${
                    filter === f
                      ? "bg-[#00ab96] text-white border-[#2ED9B8]"
                      : "bg-card text-muted-foreground border-border hover:border-[#2ED9B8]"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          {/* Enquiry summary */}
          {enquiry.length > 0 && (
            <div className="mt-6 rounded-2xl bg-[#0f2236]/70 border border-white/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1">
                <p className="text-sm font-medium text-[#2ED9B8]">Your enquiry list ({enquiry.length})</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {enquiry.map((it) => (
                    <span key={it.slug} className="inline-flex items-center gap-1 bg-card border border-white/10 rounded-full px-2.5 py-1 text-xs text-foreground">
                      {it.name}
                      <button onClick={() => removeFromEnquiry(it.slug)}><X className="h-3 w-3 text-muted-foreground hover:text-[#2ED9B8]" /></button>
                    </span>
                  ))}
                </div>
              </div>
              <Button onClick={openRFQ} className="bg-[#00ab96] hover:bg-[#0b8f7e] text-white rounded-full h-11 px-6 shrink-0">
                Request an RFQ
              </Button>
            </div>
          )}

          <p className="mt-6 text-sm text-muted-foreground">{filtered.length} results</p>

          {/* Grid */}
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
            {filtered.map((p, i) => {
              const added = isInEnquiry(p.slug);
              return (
                <Reveal key={p.slug} delay={(i % 3) * 60}>
                  <div className="group h-full bg-card rounded-2xl border border-white/10 p-6 hover:shadow-[0_24px_50px_-30px_rgba(12,97,82,0.5)] hover:border-[#2ED9B8]/25 transition-all duration-300 flex flex-col">
                    <span className="inline-block w-fit text-[11px] uppercase tracking-[0.14em] text-[#2ED9B8] bg-[#0f2236] px-2.5 py-1 rounded-full">
                      {p.group === "Dosage Forms" ? "Dosage Forms" : p.category || p.group}
                    </span>
                    <h3 className="mt-4 font-display text-[21px] text-foreground">{p.name}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed flex-1">{p.blurb}</p>
                    <div className="mt-5 flex items-center justify-between">
                      <Link to={`/products/${p.slug}`} className="tap-target text-sm font-medium text-[#2ED9B8] inline-flex items-center gap-1 hover:gap-2 transition-all">
                        Details <ArrowUpRight className="h-4 w-4" />
                      </Link>
                      <button
                        onClick={() => addToEnquiry({ slug: p.slug, name: p.name })}
                        disabled={added}
                        className={`inline-flex items-center gap-1.5 text-sm rounded-full px-3.5 h-9 border transition-colors ${
                          added ? "bg-[#0f2236] border-white/10 text-[#2ED9B8]" : "border-border text-foreground hover:border-[#2ED9B8] hover:text-[#2ED9B8]"
                        }`}
                      >
                        {added ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                        {added ? "Added" : "Enquire"}
                      </button>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="mt-10 text-center py-16 rounded-2xl border border-dashed border-border">
              <p className="text-muted-foreground">No products match your search.</p>
            </div>
          )}
        </div>
      </section>

    </>
  );
}
